import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { validateChassisNumber } from '../../../services/chassisExtractionService';
import { supabase } from '../../../lib/supabase';

const DocumentViewerModal = ({ isOpen, onClose, application, onUpdateChassisNumber }) => {
  const [chassisNumber, setChassisNumber] = useState(application?.chassisNumber || '');
  const [isLoading, setIsLoading] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const [fileType, setFileType] = useState(null);
  const [renderError, setRenderError] = useState(null);
  const [loadMethod, setLoadMethod] = useState(null);
  
  // Diagnostics
  const [diagnostics, setDiagnostics] = useState({
    originalUrl: null,
    extractedPath: null,
    signedUrl: null,
    blobUrl: null,
    expiresIn: null,
    detectedFileType: null,
    loadMethod: null,
    errors: [],
    timestamp: null
  });

  // Enhanced logging function
  const logDiagnostic = (category, message, data = null) => {
    const logEntry = {
      category,
      message,
      data,
      timestamp: new Date()?.toISOString()
    };
    console.log(`[DOCUMENT ${category}]`, message, data || '');
    setDiagnostics(prev => ({
      ...prev,
      errors: [...(prev?.errors || []), logEntry]
    }));
  };

  // Extract clean file path from storage URL
  const extractFilePath = (url) => {
    if (!url) {
      logDiagnostic('ERROR', 'No URL provided to extractFilePath');
      return null;
    }
    
    logDiagnostic('INFO', 'Original URL received', url);
    
    try {
      const cleanUrl = url?.split('?')?.[0]?.split('#')?.[0];
      logDiagnostic('INFO', 'Cleaned URL (removed query params)', cleanUrl);
      
      // Method 1: Full Supabase storage URL pattern
      if (cleanUrl?.includes('/storage/v1/object/')) {
        logDiagnostic('INFO', 'Attempting Method 1: Full Supabase storage URL pattern');
        const parts = cleanUrl?.split('/storage/v1/object/');
        if (parts?.length > 1) {
          let path = parts?.[1]?.replace(/^(public|authenticated)\//g, '');
          path = path?.replace(/^vehicle-documents\//g, '');
          logDiagnostic('SUCCESS', 'Method 1 succeeded - extracted path', path);
          return path;
        }
        logDiagnostic('WARNING', 'Method 1 failed - parts length incorrect', parts?.length);
      }
      
      // Method 2: Contains 'vehicle-documents/' anywhere
      if (cleanUrl?.includes('vehicle-documents/')) {
        logDiagnostic('INFO', 'Attempting Method 2: vehicle-documents/ pattern');
        const index = cleanUrl?.indexOf('vehicle-documents/');
        let path = cleanUrl?.substring(index + 'vehicle-documents/'?.length);
        logDiagnostic('SUCCESS', 'Method 2 succeeded - extracted path', path);
        return path;
      }
      
      // Method 3: Already a clean path
      if (!cleanUrl?.includes('http') && !cleanUrl?.includes('supabase')) {
        logDiagnostic('INFO', 'Method 3: URL appears to be clean path already', cleanUrl);
        return cleanUrl;
      }
      
      logDiagnostic('ERROR', 'All extraction methods failed', cleanUrl);
      return null;
    } catch (error) {
      logDiagnostic('ERROR', 'Exception in extractFilePath', error?.message);
      return null;
    }
  };

  // Detect file type from URL
  const detectFileType = (url) => {
    if (!url) return 'pdf';
    const urlLower = url?.toLowerCase();
    const isImage = urlLower?.match(/\.(jpg|jpeg|png|gif|bmp|webp)(\?|$)/);
    const type = isImage ? 'image' : 'pdf';
    logDiagnostic('INFO', 'File type detected', { url: urlLower, type });
    return type;
  };

  // NEW: Load document using direct blob download (most reliable method)
  const loadDocument = async () => {
    if (!application?.documentUrl) {
      const error = 'Keine Dokument-URL verfügbar';
      logDiagnostic('ERROR', error);
      setLoadError(error);
      return;
    }

    // Reset state
    setIsLoadingDoc(true);
    setLoadError(null);
    setRenderError(null);
    setDocumentUrl(null);
    setLoadMethod(null);
    setDiagnostics({
      originalUrl: application?.documentUrl,
      extractedPath: null,
      signedUrl: null,
      blobUrl: null,
      expiresIn: null,
      detectedFileType: null,
      loadMethod: null,
      errors: [],
      timestamp: new Date()?.toISOString()
    });

    logDiagnostic('INFO', '=== STARTING COMPREHENSIVE DOCUMENT LOAD ===');
    logDiagnostic('INFO', 'Application ID', application?.id);
    logDiagnostic('INFO', 'Customer Name', application?.customerName);

    try {
      // Step 1: Extract file path
      logDiagnostic('INFO', 'Step 1: Extracting file path');
      const filePath = extractFilePath(application?.documentUrl);
      
      if (!filePath) {
        throw new Error('Ungültige Dokument-URL - Pfad konnte nicht extrahiert werden');
      }
      
      setDiagnostics(prev => ({ ...prev, extractedPath: filePath }));

      // Step 2: Detect file type
      logDiagnostic('INFO', 'Step 2: Detecting file type');
      const type = detectFileType(application?.documentUrl);
      setFileType(type);
      setDiagnostics(prev => ({ ...prev, detectedFileType: type }));

      // Step 3: Try Method 1 - Direct blob download (most reliable)
      logDiagnostic('INFO', 'Step 3: Attempting Method 1 - Direct blob download');
      try {
        const { data: blobData, error: downloadError } = await supabase?.storage
          ?.from('vehicle-documents')
          ?.download(filePath);
        
        if (downloadError) {
          logDiagnostic('WARNING', 'Method 1 failed - blob download error', downloadError?.message);
          throw downloadError;
        }
        
        if (blobData) {
          // Create blob URL from downloaded data
          const blobUrl = URL.createObjectURL(blobData);
          logDiagnostic('SUCCESS', 'Method 1 succeeded - blob URL created', blobUrl?.substring(0, 50));
          
          setDocumentUrl(blobUrl);
          setLoadMethod('blob');
          setDiagnostics(prev => ({ 
            ...prev, 
            blobUrl,
            loadMethod: 'Direct Blob Download'
          }));
          
          logDiagnostic('SUCCESS', '=== DOCUMENT LOAD COMPLETED (BLOB METHOD) ===');
          setIsLoadingDoc(false);
          return;
        }
      } catch (blobError) {
        logDiagnostic('WARNING', 'Method 1 completely failed, trying Method 2', blobError?.message);
      }

      // Step 4: Try Method 2 - Signed URL (fallback)
      logDiagnostic('INFO', 'Step 4: Attempting Method 2 - Signed URL');
      const { data: signedUrlData, error: signedUrlError } = await supabase?.storage
        ?.from('vehicle-documents')
        ?.createSignedUrl(filePath, 3600); // 1 hour expiry
      
      if (signedUrlError) {
        logDiagnostic('ERROR', 'Method 2 failed - signed URL error', signedUrlError?.message);
        throw new Error(`Signed URL Fehler: ${signedUrlError.message}`);
      }
      
      if (!signedUrlData?.signedUrl) {
        logDiagnostic('ERROR', 'Method 2 failed - no signed URL received');
        throw new Error('Keine signierte URL erhalten');
      }

      logDiagnostic('SUCCESS', 'Method 2 succeeded - signed URL created');
      
      setDocumentUrl(signedUrlData?.signedUrl);
      setLoadMethod('signed');
      setDiagnostics(prev => ({ 
        ...prev, 
        signedUrl: signedUrlData?.signedUrl,
        expiresIn: '3600 seconds',
        loadMethod: 'Signed URL'
      }));
      
      logDiagnostic('SUCCESS', '=== DOCUMENT LOAD COMPLETED (SIGNED URL METHOD) ===');
      
    } catch (error) {
      logDiagnostic('ERROR', '=== ALL DOCUMENT LOAD METHODS FAILED ===', {
        message: error?.message,
        stack: error?.stack
      });
      setLoadError(error?.message || 'Fehler beim Laden des Dokuments');
    } finally {
      setIsLoadingDoc(false);
    }
  };

  // Download using best available method
  const handleDownload = async () => {
    logDiagnostic('INFO', '=== STARTING DOWNLOAD PROCESS ===');
    
    try {
      const filePath = extractFilePath(application?.documentUrl);
      if (!filePath) {
        throw new Error('Ungültige Dokument-URL');
      }
      
      // Try direct blob download first (most reliable)
      try {
        const { data: blobData, error: downloadError } = await supabase?.storage
          ?.from('vehicle-documents')
          ?.download(filePath);
        
        if (downloadError) throw downloadError;
        
        if (blobData) {
          const fileName = filePath?.split('/')?.pop();
          const extension = fileName?.includes('.') ? fileName?.split('.')?.pop() : (fileType === 'pdf' ? 'pdf' : 'jpg');
          const downloadName = `${application?.customerName?.replace(/\s+/g, '_')}_dokument.${extension}`;
          
          // Create blob URL and trigger download
          const blobUrl = URL.createObjectURL(blobData);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = downloadName;
          document.body?.appendChild(link);
          link?.click();
          document.body?.removeChild(link);
          URL.revokeObjectURL(blobUrl);
          
          logDiagnostic('SUCCESS', 'Download completed (blob method)', downloadName);
          return;
        }
      } catch (blobError) {
        logDiagnostic('WARNING', 'Blob download failed, trying signed URL', blobError?.message);
      }
      
      // Fallback to signed URL download
      const { data: signedUrlData, error: signedUrlError } = await supabase?.storage
        ?.from('vehicle-documents')
        ?.createSignedUrl(filePath, 60);
      
      if (signedUrlError || !signedUrlData?.signedUrl) {
        throw new Error('Download-URL konnte nicht erstellt werden');
      }
      
      const fileName = filePath?.split('/')?.pop();
      const extension = fileName?.includes('.') ? fileName?.split('.')?.pop() : (fileType === 'pdf' ? 'pdf' : 'jpg');
      const downloadName = `${application?.customerName?.replace(/\s+/g, '_')}_dokument.${extension}`;
      
      const link = document.createElement('a');
      link.href = signedUrlData?.signedUrl;
      link.download = downloadName;
      link.target = '_blank';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      
      logDiagnostic('SUCCESS', 'Download completed (signed URL method)', downloadName);
    } catch (error) {
      logDiagnostic('ERROR', 'Download failed', error?.message);
      alert('Fehler beim Download: ' + error?.message);
    }
  };

  // Handle rendering errors
  const handleRenderError = (errorType) => {
    const errorMessage = errorType === 'pdf' ?'PDF konnte nicht im Browser angezeigt werden' :'Bild konnte nicht geladen werden';
    logDiagnostic('ERROR', `Render error: ${errorMessage}`);
    setRenderError(errorMessage);
  };

  // Handle successful load
  const handleRenderSuccess = (type) => {
    logDiagnostic('SUCCESS', `${type} rendered successfully`);
    setRenderError(null);
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (documentUrl && loadMethod === 'blob') {
        URL.revokeObjectURL(documentUrl);
        logDiagnostic('INFO', 'Blob URL cleaned up');
      }
    };
  }, [documentUrl, loadMethod]);

  // Load document when modal opens
  useEffect(() => {
    if (isOpen && application) {
      setChassisNumber(application?.chassisNumber || '');
      setZoomLevel(1);
      setIsZoomed(false);
      setFileType(null);
      setLoadError(null);
      setRenderError(null);
      setDocumentUrl(null);
      setLoadMethod(null);
      
      loadDocument();
    }
  }, [isOpen, application?.id]);

  if (!isOpen || !application) return null;

  const handleSave = async () => {
    const validation = validateChassisNumber(chassisNumber);
    
    if (!validation?.isValid) {
      alert(validation?.message);
      return;
    }

    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    onUpdateChassisNumber(application?.id, chassisNumber);
    setIsLoading(false);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const toggleZoom = () => {
    if (isZoomed) {
      setZoomLevel(1);
      setIsZoomed(false);
    } else {
      setZoomLevel(2);
      setIsZoomed(true);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Fahrzeugschein - {application?.customerName}
            </h2>
            {fileType && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                {fileType === 'pdf' ? 'PDF' : 'Bild'}
              </span>
            )}
            {documentUrl && loadMethod && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                ✓ Geladen ({loadMethod === 'blob' ? 'Direkt' : 'Signiert'})
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Diagnostics Panel */}
        {diagnostics?.extractedPath && (
          <div className="border-b border-gray-200 bg-blue-50 p-3">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-xs font-semibold text-blue-900 mb-2">🔍 Dokument-Informationen</h4>
                <div className="grid grid-cols-4 gap-3 text-xs">
                  <div className="p-2 rounded bg-white border border-blue-200">
                    <div className="font-semibold text-gray-700">Dateipfad</div>
                    <div className="text-gray-600 font-mono text-[10px] truncate">
                      {diagnostics?.extractedPath}
                    </div>
                  </div>
                  <div className="p-2 rounded bg-white border border-blue-200">
                    <div className="font-semibold text-gray-700">Dateityp</div>
                    <div className="text-gray-600">
                      {diagnostics?.detectedFileType?.toUpperCase() || 'N/A'}
                    </div>
                  </div>
                  <div className="p-2 rounded bg-white border border-blue-200">
                    <div className="font-semibold text-gray-700">Lademethode</div>
                    <div className="text-green-600 font-semibold">
                      {diagnostics?.loadMethod || 'N/A'}
                    </div>
                  </div>
                  <div className="p-2 rounded bg-white border border-blue-200">
                    <div className="font-semibold text-gray-700">Status</div>
                    <div className="text-green-600">
                      {loadMethod === 'blob' ? '✓ Optimal' : '✓ Funktioniert'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document Viewer */}
        <div className="flex-1 overflow-auto bg-gray-100 p-4">
          {isLoadingDoc && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Dokument wird geladen...</p>
                <p className="text-xs text-gray-500 mt-2">Versuche mehrere Lademethoden...</p>
              </div>
            </div>
          )}

          {loadError && (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl">
                <div className="flex items-start gap-3 mb-4">
                  <Icon name="AlertCircle" size={24} className="text-red-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-900 mb-2">Fehler beim Laden</h3>
                    <p className="text-red-700 mb-4">{loadError}</p>
                    
                    {/* Diagnostic Information */}
                    <div className="bg-white rounded border border-red-200 p-4 mt-4">
                      <h4 className="font-semibold text-sm text-gray-900 mb-2">🔍 Diagnose-Informationen:</h4>
                      <div className="space-y-1 text-xs text-gray-700 font-mono">
                        <div><strong>Original URL:</strong> {diagnostics?.originalUrl?.substring(0, 80)}...</div>
                        <div><strong>Extrahierter Pfad:</strong> {diagnostics?.extractedPath || 'Fehlgeschlagen'}</div>
                        <div><strong>Dateityp:</strong> {diagnostics?.detectedFileType || 'Unbekannt'}</div>
                        <div><strong>Zeitstempel:</strong> {diagnostics?.timestamp}</div>
                      </div>
                      
                      {diagnostics?.errors?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <h5 className="font-semibold text-xs text-gray-900 mb-2">Fehlerprotokoll:</h5>
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {diagnostics?.errors?.slice(-10)?.map((log, idx) => (
                              <div key={idx} className="text-xs">
                                <span className={`font-semibold ${
                                  log?.category === 'ERROR' ? 'text-red-600' :
                                  log?.category === 'SUCCESS' ? 'text-green-600' :
                                  log?.category === 'WARNING' ? 'text-yellow-600' : 'text-blue-600'
                                }`}>[{log?.category}]</span>
                                <span className="text-gray-700 ml-2">{log?.message}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="RefreshCw"
                        iconPosition="left"
                        onClick={loadDocument}
                      >
                        Erneut versuchen
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Download"
                        iconPosition="left"
                        onClick={handleDownload}
                      >
                        Trotzdem herunterladen
                      </Button>
                    </div>
                    
                    <p className="text-xs text-gray-600 mt-4">
                      💡 Tipp: Öffnen Sie die Browser-Konsole (F12) für detaillierte technische Logs
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isLoadingDoc && !loadError && documentUrl && (
            <div className="flex items-center justify-center h-full">
              {/* Show render error if present */}
              {renderError && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 max-w-2xl w-full mx-4">
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 shadow-lg">
                    <div className="flex items-start gap-3">
                      <Icon name="AlertTriangle" size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-yellow-900 text-sm mb-1">Anzeigeproblem</h4>
                        <p className="text-yellow-800 text-sm mb-3">{renderError}</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDownload}
                            iconName="Download"
                            iconPosition="left"
                            className="bg-white"
                          >
                            Dokument herunterladen
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={loadDocument}
                            iconName="RefreshCw"
                            iconPosition="left"
                            className="bg-white"
                          >
                            Erneut versuchen
                          </Button>
                        </div>
                      </div>
                      <button
                        onClick={() => setRenderError(null)}
                        className="text-yellow-600 hover:text-yellow-800"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Document rendering */}
              <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                {/* PDF rendering - Chrome-compatible method */}
                {fileType === 'pdf' && (
                  <div className="bg-white shadow-lg w-full max-w-5xl" style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: 'center center',
                    transition: 'transform 0.2s ease',
                  }}>
                    <object
                      data={documentUrl}
                      type="application/pdf"
                      width="100%"
                      height="1000px"
                      className="border border-gray-300"
                      onLoad={() => handleRenderSuccess('PDF')}
                      onError={() => handleRenderError('pdf')}
                    >
                      <div className="p-8 text-center bg-gray-50">
                        <Icon name="FileText" size={48} className="mx-auto mb-4 text-gray-400" />
                        <p className="text-gray-700 mb-4">Ihr Browser kann dieses PDF nicht direkt anzeigen.</p>
                        <div className="flex gap-2 justify-center">
                          <Button
                            onClick={handleDownload}
                            iconName="Download"
                            iconPosition="left"
                          >
                            PDF herunterladen
                          </Button>
                          {loadMethod === 'signed' && (
                            <a
                              href={documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 inline-flex items-center gap-2"
                            >
                              <Icon name="ExternalLink" size={16} />
                              In neuem Tab öffnen
                            </a>
                          )}
                        </div>
                      </div>
                    </object>
                  </div>
                )}
                
                {/* Image rendering */}
                {fileType === 'image' && (
                  <div className="bg-white shadow-lg" style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: 'center center',
                    transition: 'transform 0.2s ease',
                  }}>
                    <img
                      src={documentUrl}
                      alt="Fahrzeugschein"
                      className="max-w-full h-auto border border-gray-300"
                      style={{ maxHeight: '80vh' }}
                      onLoad={() => handleRenderSuccess('Image')}
                      onError={() => handleRenderError('image')}
                    />
                  </div>
                )}
                
                {/* Fallback options */}
                <div className="mt-4 p-4 bg-white rounded border border-gray-300 shadow">
                  <p className="text-sm text-gray-700 mb-2">Alternative Anzeigeoptionen:</p>
                  <div className="flex gap-2">
                    {loadMethod === 'signed' && (
                      <a
                        href={documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm inline-flex items-center gap-2"
                      >
                        <Icon name="ExternalLink" size={16} />
                        In neuem Tab öffnen
                      </a>
                    )}
                    <Button
                      onClick={handleDownload}
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                    >
                      Herunterladen
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {loadMethod === 'blob' ?'✓ Dokument wurde direkt geladen (beste Methode)' :'💡 Diese URL ist 1 Stunde gültig und funktioniert auch in neuen Tabs'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="border-t border-gray-200 p-4 bg-white">
          <div className="flex items-center justify-between gap-4">
            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              <Button
                onClick={handleZoomOut}
                variant="outline"
                size="sm"
                disabled={zoomLevel <= 0.5}
                iconName="ZoomOut"
              />
              <span className="text-sm text-gray-600 min-w-[60px] text-center">
                {Math.round(zoomLevel * 100)}%
              </span>
              <Button
                onClick={handleZoomIn}
                variant="outline"
                size="sm"
                disabled={zoomLevel >= 3}
                iconName="ZoomIn"
              />
              <Button
                onClick={handleResetZoom}
                variant="outline"
                size="sm"
              >
                Zurücksetzen
              </Button>
            </div>

            {/* Download Button */}
            <Button
              onClick={handleDownload}
              variant="outline"
              disabled={!documentUrl}
              iconName="Download"
              iconPosition="left"
            >
              Herunterladen
            </Button>
          </div>
        </div>

        {/* Chassis Number Editor */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fahrgestellnummer (FIN)
              </label>
              <input
                type="text"
                value={chassisNumber}
                onChange={(e) => setChassisNumber(e?.target?.value?.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="z.B. WVWZZZ1KZBW123456"
                maxLength={17}
              />
            </div>
            <div className="flex gap-2 pt-6">
              <Button
                onClick={handleSave}
                disabled={isLoading || !chassisNumber}
                iconName="Save"
              >
                {isLoading ? 'Speichern...' : 'Speichern'}
              </Button>
              <Button onClick={onClose} variant="outline" iconName="X">
                Schließen
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewerModal;